import { useEffect, useMemo, useState } from 'react';
import { LayoutTemplate, Loader2 } from 'lucide-react';
import { useApplyTaskTemplate, useTaskTemplates } from '../../hooks/useTaskTemplates';
import { ApiError } from '../../lib/api';
import { PRIORITY_BADGE } from './taskConstants';
import { Select } from '../ui/Select';
import { useI18n } from '../../contexts/I18nContext';

interface ApplyTemplateModalProps {
  /** The project the template's tasks are created in. */
  projectId: string;
  onClose: () => void;
  /** Called with the number of tasks created, after a successful apply. */
  onApplied?: (createdCount: number) => void;
}

/**
 * "Aplică șablon task-uri" — pick a task template and instantiate its tasks on
 * the current project (POST /api/task-templates/:id/apply).
 *
 * Templates are authored in Setări → Șabloane sarcini and could previously only
 * be applied at project creation time; this is the same operation on an
 * existing project. The picked template is previewed before applying because
 * the action is additive and not undoable in one click — a wrong pick leaves N
 * stray tasks to delete by hand.
 */
export function ApplyTemplateModal({ projectId, onClose, onApplied }: ApplyTemplateModalProps) {
  const { t } = useI18n();
  const { data: templates, isLoading, isError } = useTaskTemplates();
  const apply = useApplyTaskTemplate();

  const [templateId, setTemplateId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const options = useMemo(
    () =>
      (templates ?? []).map((tpl) => ({
        value: tpl.id,
        label: tpl.name,
        description: t('tasks.applyTemplate.itemCount', { count: tpl.itemCount }),
        // A template with no items would create nothing; let it be visible but
        // not selectable rather than silently applying to zero effect.
        disabled: tpl.itemCount === 0,
      })),
    [templates, t],
  );

  const selected = (templates ?? []).find((tpl) => tpl.id === templateId);

  // Preselect when there is exactly one usable template: the dropdown would
  // otherwise be a single-choice formality.
  useEffect(() => {
    if (templateId) return;
    const usable = (templates ?? []).filter((tpl) => tpl.itemCount > 0);
    if (usable.length === 1) setTemplateId(usable[0].id);
  }, [templates, templateId]);

  function submit() {
    if (!selected || apply.isPending) return;
    setError(null);
    apply.mutate(
      { id: selected.id, projectId },
      {
        onSuccess: (result) => {
          onApplied?.(result.createdCount);
          onClose();
        },
        onError: (err) => {
          setError(
            err instanceof ApiError && err.status === 403
              ? t('tasks.applyTemplate.forbidden')
              : t('tasks.applyTemplate.error'),
          );
        },
      },
    );
  }

  return (
    <div className="ui-modal-backdrop" onClick={onClose}>
      <div className="ui-modal-panel w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {t('tasks.applyTemplate.title')}
            </h2>
            <p className="mt-1 text-sm text-gray-500">{t('tasks.applyTemplate.subtitle')}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label={t('common.close')}
          >
            ✕
          </button>
        </div>

        <div className="mt-4">
          <Select
            label={t('tasks.applyTemplate.field')}
            searchable
            clearable
            loading={isLoading}
            value={templateId}
            onChange={(value) => {
              setTemplateId(value);
              setError(null);
            }}
            placeholder={t('tasks.applyTemplate.placeholder')}
            emptyText={t('tasks.applyTemplate.none')}
            options={options}
          />
        </div>

        {isError && (
          <p className="mt-3 text-sm text-red-600">{t('tasks.applyTemplate.loadError')}</p>
        )}

        {/* Preview of exactly what will be created, in template order. */}
        {selected && (
          <div className="mt-4 rounded border border-gray-200 bg-gray-50 p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              {t('tasks.applyTemplate.preview', { count: selected.itemCount })}
            </p>
            <ul className="mt-2 max-h-56 space-y-1.5 overflow-y-auto">
              {selected.items.map((item, i) => (
                <li key={item.id} className="flex items-start gap-2 text-sm text-gray-700">
                  <span className="w-5 shrink-0 text-right text-xs text-gray-400">{i + 1}.</span>
                  <span className="min-w-0 flex-1 break-words">{item.title}</span>
                  {item.priority !== 'NONE' && (
                    <span
                      className={`shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium ${PRIORITY_BADGE[item.priority]}`}
                    >
                      {t(`tasks.priority.${item.priority}`)}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="ui-btn-secondary">
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!selected || apply.isPending}
            className="ui-btn-primary inline-flex items-center gap-1.5"
          >
            {apply.isPending && <Loader2 size={15} className="animate-spin" />}
            <LayoutTemplate size={15} />
            {t('tasks.applyTemplate.submit')}
          </button>
        </div>
      </div>
    </div>
  );
}
